import PropTypes from 'prop-types';
import { motion } from 'framer-motion';

/**
 * ThreadPeopleBadge — the count pill beside the back chevron, iMessage's
 * back-to-list shape (user design 2026-08-16).
 *
 * It replaces the labelled "Your people" link, and the win is that it needs no
 * copy at all: the NUMBER IS THE RECEIPT. Someone who just scanned a stranger's
 * QR wants evidence that something was captured, and "1" says it in a glyph
 * that every phone owner already reads. Three rounds of wording died trying to
 * say the same thing in words.
 *
 * It counts ALL connections, not unread — a borrowed affordance, deliberately
 * (user ruling): at Anon tier there is no unread concept surfaced anywhere, and
 * the total is the meaningful number. ANON ONLY; the Profile side has its own
 * navigation and no need for a receipt.
 *
 * A PILL, not a disc (user, 2026-08-20). At 26×26 it was a status dot with a
 * digit in it, and status dots aren't tapped — widening it to 42 is the whole
 * difference between a count and a control. Height stays 26 so it still sits
 * on the header's baseline beside the chevron; only the horizontal grows, which
 * is also what keeps a two- or three-digit count from changing its shape.
 *
 * Animates open on first appearance, pushing the peer's avatar right — the
 * movement is what makes it noticed without anything shouting.
 */

// Wide enough to read as a button, narrow enough to stay a badge. Also the
// animate-open target — the width IS the entrance.
const BADGE_WIDTH = 42;
const ThreadPeopleBadge = ({ count, onClick }) => {
  if (!count) return null;

  return (
    <motion.button
      initial={{ width: 0, opacity: 0, marginRight: 0 }}
      animate={{ width: BADGE_WIDTH, opacity: 1, marginRight: 8 }}
      transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1], delay: 0.15 }}
      onClick={onClick}
      aria-label={`${count} ${count === 1 ? 'connection' : 'connections'}`}
      style={{
        height: '26px',
        // No horizontal padding, and NO minWidth: the animated width owns the
        // size outright. A `minWidth: BADGE_WIDTH` here (briefly shipped
        // 2026-08-20, caught at /go:review) silently kills the entrance —
        // CSS min-width beats width, so `initial={{ width: 0 }}` computes to
        // 42px on the first frame and the badge fades in instead of growing.
        // It was guarding a three-digit count against `overflow: hidden`, a
        // case an Anon's connection list does not reach; the 26px original had
        // no such guard either.
        padding: 0,
        borderRadius: '999px',
        border: 'none',
        backgroundColor: '#000000',
        color: '#FFFFFF',
        fontSize: '14px',
        fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
        cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {count}
    </motion.button>
  );
};

ThreadPeopleBadge.propTypes = {
  count: PropTypes.number,
  onClick: PropTypes.func.isRequired,
};

export default ThreadPeopleBadge;
